import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Building2, Users, ChevronDown, ChevronRight, Search,
  AlertTriangle, User, Mail, Phone, Briefcase, Shield,
  ExternalLink, Filter, BarChart3
} from "lucide-react";

const STATUS_CONFIG = {
  alta: { label: "Alta", dot: "bg-emerald-500" },
  baja_medica: { label: "Baja medica", dot: "bg-red-500" },
  vacaciones: { label: "Vacaciones", dot: "bg-sky-500" },
  excedencia: { label: "Excedencia", dot: "bg-amber-500" },
  baja_definitiva: { label: "Baja definitiva", dot: "bg-slate-400" },
  baja_voluntaria: { label: "Baja voluntaria", dot: "bg-slate-400" },
};

function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.alta;
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${cfg.dot} flex-shrink-0`} />
      <span className="text-[10px] text-slate-500">{cfg.label}</span>
    </span>
  );
}

// ════════════════════════════════
// EMPLOYEE CARD
// ════════════════════════════════
function EmpCard({ emp, compact = false, onNavigate }) {
  const hasIncidents = emp.has_incidents;
  return (
    <div
      className={`group relative flex items-start gap-2.5 p-2.5 rounded border transition-all cursor-pointer
        ${hasIncidents ? "border-amber-200 bg-amber-50/40 hover:border-amber-300" : "border-slate-100 bg-white hover:border-slate-300 hover:shadow-sm"}`}
      onClick={() => onNavigate(emp.id)}
      data-testid={`emp-card-${emp.id}`}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-sm flex items-center justify-center text-xs font-bold
        ${hasIncidents ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
        {(emp.name || "?")[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold truncate leading-tight">{emp.name}</p>
          {hasIncidents && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />}
        </div>
        {emp.position && <p className="text-[10px] text-slate-500 truncate">{emp.position}</p>}
        <StatusDot status={emp.status} />
        {!compact && emp.email && (
          <p className="text-[10px] text-slate-400 truncate mt-0.5">{emp.email}</p>
        )}
      </div>
      <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

// ════════════════════════════════
// MANAGER NODE
// ════════════════════════════════
function ManagerNode({ mgr, onNavigate, defaultExpanded = true }) {
  const [open, setOpen] = useState(defaultExpanded);
  const isWarning = mgr.is_warning;
  const count = mgr.employees?.length || 0;

  return (
    <div className="ml-4 border-l-2 border-slate-200 pl-3" data-testid={`mgr-node-${mgr.id}`}>
      <div
        className={`flex items-center gap-2 py-1.5 cursor-pointer select-none
          ${isWarning ? "text-amber-600" : "text-slate-700"}`}
        onClick={() => setOpen(!open)}
      >
        {count > 0 ? (
          open ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        ) : <span className="w-3.5" />}
        <Shield className={`w-3.5 h-3.5 flex-shrink-0 ${isWarning ? "text-amber-500" : "text-blue-500"}`} />
        <span className="text-xs font-semibold">{mgr.name}</span>
        {mgr.position && <span className="text-[10px] text-slate-400">({mgr.position})</span>}
        {mgr.status && !isWarning && <StatusDot status={mgr.status} />}
        <Badge variant="outline" className="text-[9px] ml-1 rounded-sm">{count}</Badge>
        {isWarning && <AlertTriangle className="w-3 h-3 text-amber-500" />}
        {!isWarning && mgr.id !== "__none__" && (
          <Button variant="ghost" size="icon" className="h-5 w-5 ml-auto opacity-0 group-hover:opacity-100"
            onClick={e => { e.stopPropagation(); onNavigate(mgr.id); }}
            data-testid={`mgr-link-${mgr.id}`}>
            <ExternalLink className="w-3 h-3 text-slate-400" />
          </Button>
        )}
      </div>
      {open && count > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-1 mb-2 ml-5">
          {mgr.employees.map(e => <EmpCard key={e.id} emp={e} compact onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════
// DEPARTMENT NODE
// ════════════════════════════════
function DeptNode({ dept, onNavigate, defaultExpanded = true }) {
  const [open, setOpen] = useState(defaultExpanded);
  const isWarning = dept.is_warning;
  const totalEmps = dept.managers?.reduce((sum, m) => sum + (m.employees?.length || 0) + (m.id !== "__none__" ? 1 : 0), 0) || 0;

  return (
    <div className="ml-4 border-l-2 border-blue-100 pl-3 mb-2" data-testid={`dept-node-${dept.id}`}>
      <div
        className={`flex items-center gap-2 py-2 cursor-pointer select-none group
          ${isWarning ? "text-amber-600" : "text-slate-800"}`}
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
        <Briefcase className={`w-4 h-4 flex-shrink-0 ${isWarning ? "text-amber-500" : "text-indigo-500"}`} />
        <span className="text-sm font-semibold">{dept.name}</span>
        <Badge variant="outline" className="text-[9px] rounded-sm">{totalEmps} personas</Badge>
        {isWarning && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
      </div>
      {open && (
        <div>
          {dept.managers?.map(m => (
            <ManagerNode key={m.id} mgr={m} onNavigate={onNavigate} defaultExpanded={totalEmps <= 15} />
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════
// CENTER NODE
// ════════════════════════════════
function CenterNode({ center, onNavigate, defaultExpanded = true }) {
  const [open, setOpen] = useState(defaultExpanded);
  const isWarning = center.is_warning;
  const totalDepts = center.departments?.length || 0;

  return (
    <Card className={`rounded-sm mb-3 ${isWarning ? "border-amber-300 bg-amber-50/30" : "border-slate-200"}`} data-testid={`center-node-${center.id}`}>
      <CardHeader
        className={`pb-2 cursor-pointer select-none ${isWarning ? "bg-amber-50/50" : "bg-slate-50/50"}`}
        onClick={() => setOpen(!open)}
      >
        <CardTitle className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Building2 className={`w-5 h-5 ${isWarning ? "text-amber-500" : "text-slate-700"}`} />
          <span className="text-sm font-bold uppercase tracking-wider">{center.name}</span>
          <Badge variant="outline" className="text-[10px] rounded-sm ml-1">{totalDepts} deptos</Badge>
          {isWarning && <Badge variant="outline" className="text-[9px] rounded-sm border-amber-300 text-amber-600">Alerta</Badge>}
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="pt-2 pb-3">
          {center.departments?.map(d => (
            <DeptNode key={d.id} dept={d} onNavigate={onNavigate} defaultExpanded={totalDepts <= 6} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

// ════════════════════════════════
// MAIN PAGE
// ════════════════════════════════
export default function OrgChartPage() {
  const { axiosAuth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fCenter, setFCenter] = useState("all");
  const [fDept, setFDept] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fSearch, setFSearch] = useState("");
  const [fOnlyManagers, setFOnlyManagers] = useState(false);
  const [fOnlyNoManager, setFOnlyNoManager] = useState(false);
  const [fOnlyIncidents, setFOnlyIncidents] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/orgchart");
      setData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { load(); }, [load]);

  const goToEmployee = (empId) => navigate(`/admin/empleados?edit=${empId}`);

  // Apply client-side filters to the tree
  const filterTree = (tree) => {
    if (!tree) return [];
    const s = fSearch.toLowerCase();

    return tree
      .filter(center => fCenter === "all" || center.id === fCenter)
      .map(center => ({
        ...center,
        departments: center.departments
          .filter(dept => fDept === "all" || dept.id === fDept)
          .map(dept => ({
            ...dept,
            managers: dept.managers.map(mgr => ({
              ...mgr,
              employees: mgr.employees.filter(e => {
                if (fStatus !== "all" && e.status !== fStatus) return false;
                if (fOnlyNoManager && mgr.id !== "__none__") return false;
                if (fOnlyIncidents && !e.has_incidents) return false;
                if (s && !e.name.toLowerCase().includes(s) && !(e.position || "").toLowerCase().includes(s)) return false;
                return true;
              }),
            })).filter(mgr => {
              if (fOnlyManagers && mgr.id === "__none__") return false;
              // Keep manager node if it has employees or if manager matches search
              if (mgr.id !== "__none__" && s && mgr.name.toLowerCase().includes(s)) return true;
              if (fStatus !== "all" && mgr.id !== "__none__" && mgr.status !== fStatus) {
                // But keep if subordinates match
                return mgr.employees.length > 0;
              }
              return mgr.employees.length > 0 || (mgr.id !== "__none__" && !fOnlyNoManager);
            }),
          })).filter(dept => dept.managers.length > 0),
      })).filter(center => center.departments.length > 0);
  };

  const filteredTree = filterTree(data?.tree);
  const hasActiveFilters = fCenter !== "all" || fDept !== "all" || fStatus !== "all" || fSearch || fOnlyManagers || fOnlyNoManager || fOnlyIncidents;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const incidents = data?.incidents || [];

  return (
    <div className="space-y-5 max-w-7xl mx-auto" data-testid="orgchart-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold uppercase tracking-wider text-slate-800">Organigrama</h1>
          <p className="text-xs text-slate-500 mt-0.5">Estructura organizativa de ASCAUTO</p>
        </div>
        <Button variant="outline" size="sm" className="text-xs rounded-sm gap-1.5"
          onClick={() => setShowFilters(!showFilters)} data-testid="btn-toggle-filters">
          <Filter className="w-3.5 h-3.5" />
          Filtros
          {hasActiveFilters && <Badge className="bg-blue-600 text-white text-[8px] rounded-full h-4 w-4 p-0 flex items-center justify-center ml-0.5">!</Badge>}
        </Button>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="orgchart-summary">
        {[
          { label: "Empleados", value: summary.total_employees, cls: "text-slate-700 bg-slate-50 border-slate-200" },
          { label: "Centros", value: summary.total_centers, cls: "text-blue-700 bg-blue-50 border-blue-200" },
          { label: "Departamentos", value: summary.total_departments, cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
          { label: "Sin centro", value: summary.sin_centro, cls: summary.sin_centro > 0 ? "text-amber-700 bg-amber-50 border-amber-300" : "text-green-700 bg-green-50 border-green-200" },
          { label: "Sin depto", value: summary.sin_departamento, cls: summary.sin_departamento > 0 ? "text-amber-700 bg-amber-50 border-amber-300" : "text-green-700 bg-green-50 border-green-200" },
          { label: "Sin jefe", value: summary.sin_jefe, cls: summary.sin_jefe > 0 ? "text-amber-700 bg-amber-50 border-amber-300" : "text-green-700 bg-green-50 border-green-200" },
          { label: "Sin puesto", value: summary.sin_puesto, cls: summary.sin_puesto > 0 ? "text-amber-700 bg-amber-50 border-amber-300" : "text-green-700 bg-green-50 border-green-200" },
        ].map(k => (
          <Card key={k.label} className={`border rounded-sm ${k.cls}`}>
            <CardContent className="p-2.5 text-center">
              <p className="text-xl font-bold font-mono">{k.value}</p>
              <p className="text-[9px] uppercase tracking-wider font-semibold">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters panel */}
      {showFilters && (
        <Card className="border-slate-200 rounded-sm" data-testid="orgchart-filters">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Centro</Label>
                <Select value={fCenter} onValueChange={setFCenter}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="filter-center"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {data?.filters?.centers?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Departamento</Label>
                <Select value={fDept} onValueChange={setFDept}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="filter-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {data?.filters?.departments?.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Situacion</Label>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={fSearch} onChange={e => setFSearch(e.target.value)}
                    placeholder="Nombre o puesto..."
                    className="pl-7 h-8 text-xs rounded-sm" data-testid="filter-search" />
                </div>
              </div>
              <div className="space-y-2.5 col-span-2 sm:col-span-1 lg:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Opciones</Label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={fOnlyManagers} onCheckedChange={setFOnlyManagers} className="scale-75" />
                    <span className="text-[10px] text-slate-600">Solo mandos</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={fOnlyNoManager} onCheckedChange={setFOnlyNoManager} className="scale-75" />
                    <span className="text-[10px] text-slate-600">Sin jefe</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Switch checked={fOnlyIncidents} onCheckedChange={setFOnlyIncidents} className="scale-75" />
                    <span className="text-[10px] text-slate-600">Incidencias</span>
                  </label>
                </div>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <p className="text-[10px] text-slate-500">{filteredTree.reduce((s, c) => s + c.departments.reduce((s2, d) => s2 + d.managers.reduce((s3, m) => s3 + m.employees.length + (m.id !== "__none__" ? 1 : 0), 0), 0), 0)} resultados</p>
                <Button variant="ghost" size="sm" className="text-[10px] h-6 text-slate-400" onClick={() => { setFCenter("all"); setFDept("all"); setFStatus("all"); setFSearch(""); setFOnlyManagers(false); setFOnlyNoManager(false); setFOnlyIncidents(false); }}>
                  Limpiar filtros
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Incidents panel */}
      {incidents.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30 rounded-sm" data-testid="orgchart-incidents">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wider font-semibold text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Incidencias estructurales ({incidents.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
              {incidents.map((inc, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-white rounded-sm border border-amber-100 text-xs"
                  data-testid={`incident-${i}`}>
                  <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-slate-700 truncate block">{inc.name}</span>
                    <span className="text-[10px] text-amber-600">{inc.msg}</span>
                  </div>
                  {inc.employee_id && (
                    <Button variant="ghost" size="icon" className="h-5 w-5 flex-shrink-0"
                      onClick={() => goToEmployee(inc.employee_id)}>
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tree */}
      <div data-testid="orgchart-tree">
        {filteredTree.length === 0 ? (
          <Card className="border-slate-200 rounded-sm">
            <CardContent className="p-8 text-center text-slate-400 text-xs">
              Sin resultados para los filtros aplicados
            </CardContent>
          </Card>
        ) : (
          filteredTree.map(c => <CenterNode key={c.id} center={c} onNavigate={goToEmployee} />)
        )}
      </div>
    </div>
  );
}
